import type { z } from 'zod';
import type { ProviderId, Tier, TileSize, TileType } from './constants.js';
import { DATA_SHAPE_KEYS } from './data-shapes.js';
import {
  weatherParamsSchema,
  marketsParamsSchema,
  newsParamsSchema,
  sportsParamsSchema,
  clockParamsSchema,
  calendarParamsSchema,
  noteParamsSchema,
  todoParamsSchema,
  countdownParamsSchema,
  rssParamsSchema,
} from './params.js';

/**
 * The tile-type registry — the SINGLE SOURCE OF TRUTH (ADR-002 §3.1).
 *
 * Adding a tile type = adding one entry here (+ its adapter, per ADR-001 build
 * order). Everything else — the discriminated schema, the provider allowlist per
 * type, the glance validator, the refresh floor — reads from this table. The
 * `tier` here is AUTHORITATIVE: a cloned config claiming `pro` is ignored and the
 * registry value wins (ADR-002 §7 risk #3).
 *
 * TTLs are the shared-cache refresh floors from ADR-001 §3 (weather 15m, quotes
 * 60s delayed, news 10m, sports 60s). `refreshBounds` is the client poll cadence
 * window; `min` is clamped to `ttlSecFloor` in the schema so a config can never
 * poll faster than the cache refills (protects D-007).
 */
export interface TileTypeDef {
  readonly type: TileType;
  /** Authoritative capability tier. Config-supplied tier is ignored (§7 #3). */
  readonly tier: Tier;
  /** Providers this type may name. Empty for no-fetch types (clock). */
  readonly allowedProviders: readonly ProviderId[];
  /** Display variants legal for this type (ADR-002 §3). */
  readonly allowedVariants: readonly string[];
  /** Grid sizes this type supports (subset of TILE_SIZES). */
  readonly allowedSizes: readonly TileSize[];
  /**
   * Strict Zod schema for `source.params`. MUST be non-null for any instantiable
   * fetch tile — a fetch tile with a null schema is rejected at parse time
   * (fail-closed; ADR-002 §7 #1). Null is reserved for types whose params are
   * deferred to a later phase (e.g. brief.ai / alert.generic, whose configs are
   * synthesized server-side and are not user-instantiable in v1.5), which
   * therefore cannot be instantiated as fetch tiles yet.
   */
  readonly paramsSchema: z.ZodTypeAny | null;
  /** Legal glance metric keys (ADR-002 §3.3). */
  readonly dataShape: readonly string[];
  /** Shared-cache TTL floor in seconds (ADR-001 §3). 0 = no fetch. */
  readonly ttlSecFloor: number;
  /** Client poll cadence bounds [min,max]; min is clamped ≥ ttlSecFloor. */
  readonly refreshBounds: readonly [min: number, max: number];
  /**
   * True for tiles with no upstream fetch AT ALL (e.g. clock.local computes from
   * the local `Date`). NARROWER than `clientDirect`: `noFetch ⊂ clientDirect`.
   * Existing consumers (use-tile-view-model, tile-forms) branch on this to mean
   * "compute locally, no network" — kept unchanged for backward compat.
   */
  readonly noFetch: boolean;
  /**
   * True for CLIENT-DIRECT tiles (ADR-004): the renderer routes them to a LOCAL
   * data provider, NEVER through the CacheClient / shared cache. Two members today:
   *   - `calendar.peek` — personal data fetched browser → Google API directly with
   *     the user's own OAuth token; nothing personal transits our Lambda/DynamoDB.
   *   - `clock.local`   — the degenerate no-network case (its provider is a `Date`).
   * This is the ONE mechanism the renderer special-cases on, so clock and calendar
   * share a single code path rather than two. Shared-data tiles set `false` and keep
   * the proxy path (ADR-003). A `clientDirect` tile's config is PII/token-free and
   * still derives a stable cache key for board serialization (it just never keys the
   * shared cache). `noFetch` is the subset that additionally does no fetch of any
   * kind; `calendar.peek` is `clientDirect: true` but `noFetch: false`.
   */
  readonly clientDirect: boolean;
  /**
   * True for USER-SOURCE tiles (D-017 / ADR-005): a type whose params carry a
   * user-supplied https URL (via `userSourceUrlSchema`) that is fetched CLIENT-SIDE
   * only. One member today: `rss.feed`. This is a STRICT SUBSET of `clientDirect`
   * (userSource ⊂ clientDirect) — a user-source tile is always client-direct (its
   * URL is fetched by the user's browser, never our Lambda; the arbitrary-URL proxy
   * is out until Phase 4). Two flags, not one, because they answer different
   * questions: `clientDirect` = "routed to a local provider, not the shared cache";
   * `userSource` = "its config contains a user-supplied URL, so it is PRIVATE for
   * clone-a-board (a tracking-beacon vector — ADR-005 §Privacy) and is the scoped
   * exception to ADR-002's no-URLs rule". Every other type sets `false`.
   * Optional so existing entries need not restate the default; absent ⇒ false.
   */
  readonly userSource?: boolean;
}

const DAY = 24 * 60 * 60;

export const TILE_REGISTRY = {
  'weather.current': {
    type: 'weather.current',
    tier: 'free',
    allowedProviders: ['openweathermap', 'nws'],
    allowedVariants: ['big-number', 'forecast'],
    allowedSizes: ['1x1', '2x1', '2x2'],
    paramsSchema: weatherParamsSchema,
    dataShape: DATA_SHAPE_KEYS['weather.current'],
    ttlSecFloor: 900, // 15m — ADR-001 §3
    refreshBounds: [900, DAY],
    noFetch: false,
    clientDirect: false,
  },
  'markets.quote': {
    type: 'markets.quote',
    tier: 'free',
    allowedProviders: ['finnhub', 'twelvedata'],
    allowedVariants: ['big-number', 'sparkline', 'list'],
    allowedSizes: ['1x1', '2x1', '2x2'],
    paramsSchema: marketsParamsSchema,
    dataShape: DATA_SHAPE_KEYS['markets.quote'],
    ttlSecFloor: 60, // 60s delayed — ADR-001 §3
    refreshBounds: [60, DAY],
    noFetch: false,
    clientDirect: false,
  },
  'news.headlines': {
    type: 'news.headlines',
    tier: 'free',
    allowedProviders: ['gnews'],
    allowedVariants: ['list', 'headline'],
    allowedSizes: ['2x1', '2x2'],
    paramsSchema: newsParamsSchema,
    dataShape: DATA_SHAPE_KEYS['news.headlines'],
    ttlSecFloor: 600, // 10m — ADR-001 §3
    refreshBounds: [600, DAY],
    noFetch: false,
    clientDirect: false,
  },
  'sports.score': {
    type: 'sports.score',
    tier: 'free',
    allowedProviders: ['thesportsdb'],
    allowedVariants: ['big-number', 'list'],
    allowedSizes: ['1x1', '2x1', '2x2'],
    paramsSchema: sportsParamsSchema,
    dataShape: DATA_SHAPE_KEYS['sports.score'],
    ttlSecFloor: 60, // 60s per in-progress game — ADR-001 §3
    refreshBounds: [60, DAY],
    noFetch: false,
    clientDirect: false,
  },
  'clock.local': {
    type: 'clock.local',
    tier: 'free',
    allowedProviders: [], // no fetch
    allowedVariants: ['big-number'],
    allowedSizes: ['1x1', '2x1'],
    paramsSchema: clockParamsSchema,
    dataShape: DATA_SHAPE_KEYS['clock.local'],
    ttlSecFloor: 0,
    refreshBounds: [1, 60], // local re-render cadence, not a fetch
    noFetch: true,
    // clock is the degenerate client-direct case: its "provider" is a local Date.
    // Marked clientDirect so the renderer has ONE local-provider path (ADR-004).
    clientDirect: true,
  },
  'calendar.peek': {
    type: 'calendar.peek',
    // FREE: client-direct means zero cost to us (no proxy, no upstream key we pay
    // for) — personal data is fetched by the user's own client (ADR-004). No cost
    // gate is warranted, so it ships free.
    tier: 'free',
    allowedProviders: ['google'],
    allowedVariants: ['list'],
    allowedSizes: ['2x1', '2x2'],
    // Unlocked in v1.5 (ADR-004): primary-only, bounded params. The tile now passes
    // the fail-closed instantiation guard (it has a real params schema).
    paramsSchema: calendarParamsSchema,
    dataShape: DATA_SHAPE_KEYS['calendar.peek'],
    // TTL floor is the CLIENT poll cadence for the browser→Google fetch, not a
    // shared-cache refill (clientDirect tiles never touch the shared cache). Kept as
    // a sane minimum so the tile can't hammer Google from a hand-edited config.
    ttlSecFloor: 300,
    refreshBounds: [300, DAY],
    noFetch: false, // it DOES fetch — just browser→Google, not through our proxy
    clientDirect: true, // ADR-004: personal data, never through the CacheClient
  },
  // ---- Local utility widgets (D-015) ----
  // The cheapest breadth win: zero data cost, retention-positive daily utility.
  // Their params ARE the storage (localStorage board config), edited via
  // configureTile, computed into display data locally — like clock. They are
  // BOTH noFetch (no network of any kind) AND clientDirect (routed to a local
  // provider, never the shared cache); noFetch ⊂ clientDirect, same as clock.
  // allowedProviders is [] (the no-fetch invariant); a source, if present, uses
  // the 'local' sentinel exactly like clock.local. ttlSecFloor is 0 (no cache to
  // refill); refreshBounds is a local re-render cadence, not a fetch cadence —
  // countdown re-renders often to keep the elapsed/remaining figure live, note
  // and todo change only on edit so they re-render lazily.
  'note.sticky': {
    type: 'note.sticky',
    tier: 'free',
    allowedProviders: [], // no fetch
    allowedVariants: ['text'],
    allowedSizes: ['1x1', '2x1', '2x2'],
    paramsSchema: noteParamsSchema,
    dataShape: DATA_SHAPE_KEYS['note.sticky'],
    ttlSecFloor: 0,
    refreshBounds: [1, DAY], // local re-render only; content changes on edit
    noFetch: true,
    clientDirect: true,
  },
  'todo.list': {
    type: 'todo.list',
    tier: 'free',
    allowedProviders: [], // no fetch
    allowedVariants: ['list'],
    allowedSizes: ['2x1', '2x2'],
    paramsSchema: todoParamsSchema,
    dataShape: DATA_SHAPE_KEYS['todo.list'],
    ttlSecFloor: 0,
    refreshBounds: [1, DAY], // local re-render only; content changes on edit
    noFetch: true,
    clientDirect: true,
  },
  'countdown.event': {
    type: 'countdown.event',
    tier: 'free',
    allowedProviders: [], // no fetch
    allowedVariants: ['big-number'],
    allowedSizes: ['1x1', '2x1'],
    paramsSchema: countdownParamsSchema,
    dataShape: DATA_SHAPE_KEYS['countdown.event'],
    ttlSecFloor: 0,
    refreshBounds: [1, DAY], // re-renders to keep the elapsed/remaining figure live
    noFetch: true,
    clientDirect: true,
  },
  // ---- User-source tiles (D-017 / ADR-005) — the FOURTH tile class ----
  // A user-supplied https feed URL (params.feedUrl via userSourceUrlSchema) fetched
  // CLIENT-SIDE ONLY (the user's own browser, NEVER our Lambda — the arbitrary-URL
  // proxy is out until a hardened fetcher exists, Phase 4). So it is clientDirect
  // (routed to a local RSS provider, never the shared cache) but NOT noFetch (it
  // DOES fetch — browser→feed, like calendar.peek fetches browser→Google), and it
  // is the sole userSource type. provider 'user' is the sentinel (the URL is in
  // params, not a server allowlist entry). tier free: zero cost to us (no proxy, no
  // key we pay for). ttlSecFloor/refreshBounds mirror news (600s..DAY) — an RSS feed
  // has the same "check every ~10 min" cadence; here the floor is the CLIENT poll
  // cadence (clientDirect tiles never refill a shared cache), a sane minimum so a
  // hand-edited config can't hammer the feed. dataShape mirrors news.headlines.
  'rss.feed': {
    type: 'rss.feed',
    tier: 'free',
    allowedProviders: ['user'], // the user-source sentinel; URL lives in params
    allowedVariants: ['list'],
    allowedSizes: ['2x1', '2x2'],
    paramsSchema: rssParamsSchema,
    dataShape: DATA_SHAPE_KEYS['rss.feed'],
    ttlSecFloor: 600, // 10m client poll cadence (mirrors news; not a shared-cache refill)
    refreshBounds: [600, DAY],
    noFetch: false, // it DOES fetch — browser→feed, not through our proxy
    clientDirect: true, // ADR-005: user URL fetched by the browser, never our Lambda
    userSource: true, // the sole user-source tile: config carries a user URL (PRIVATE)
  },
  'markets.realtime': {
    type: 'markets.realtime',
    tier: 'pro',
    allowedProviders: ['finnhub', 'twelvedata'],
    allowedVariants: ['big-number', 'sparkline', 'list'],
    allowedSizes: ['1x1', '2x1', '2x2'],
    paramsSchema: marketsParamsSchema,
    dataShape: DATA_SHAPE_KEYS['markets.realtime'],
    ttlSecFloor: 15,
    refreshBounds: [15, DAY],
    noFetch: false,
    clientDirect: false,
  },
  'brief.ai': {
    type: 'brief.ai',
    tier: 'pro',
    allowedProviders: [], // synthesized server-side across the user's tiles
    allowedVariants: ['list'],
    allowedSizes: ['2x2'],
    paramsSchema: null,
    dataShape: DATA_SHAPE_KEYS['brief.ai'],
    ttlSecFloor: 3600,
    refreshBounds: [3600, DAY],
    noFetch: false,
    clientDirect: false,
  },
  'alert.generic': {
    type: 'alert.generic',
    tier: 'pro',
    allowedProviders: [],
    allowedVariants: ['big-number'],
    allowedSizes: ['1x1', '2x1'],
    paramsSchema: null,
    dataShape: DATA_SHAPE_KEYS['alert.generic'],
    ttlSecFloor: 60,
    refreshBounds: [60, DAY],
    noFetch: false,
    clientDirect: false,
  },
} as const satisfies Record<TileType, TileTypeDef>;

/** Look up a registry entry by type. Total over TileType (registry is exhaustive). */
export function getTileTypeDef(type: TileType): TileTypeDef {
  return TILE_REGISTRY[type];
}
